// Module Vuex pour timeline
import dexieService from '@/services/dexieService'
import { RENTABLE_POINTS } from '@/utils/constants'

// Créer un mapping local des valeurs rentable pour fallback
const RENTABLE_MAPPING = RENTABLE_POINTS.reduce((acc, point) => {
  acc[point.value] = point.label
  return acc
}, {})

const state = {
  events: [],
  loading: false,
  error: null,
  currentPropertyId: null
}

const mutations = {
  SET_EVENTS(state, events) {
    state.events = events
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  SET_CURRENT_PROPERTY_ID(state, propertyId) {
    state.currentPropertyId = propertyId
  },
  ADD_EVENT(state, event) {
    state.events.push(event)
  },
  UPDATE_EVENT(state, updatedEvent) {
    const index = state.events.findIndex(event => event.id === updatedEvent.id)
    if (index !== -1) {
      state.events.splice(index, 1, updatedEvent)
    }
  },
  REMOVE_EVENT(state, eventId) {
    state.events = state.events.filter(event => event.id !== eventId)
  }
}

const actions = {
  async fetchTimelineEvents({ commit, rootState }, propertyId = null) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      // Utiliser propertyId passé en paramètre ou celui de la route
      const targetPropertyId = propertyId || rootState.route?.params?.id
      
      if (!targetPropertyId) {
        console.warn('Aucun ID de propriété fourni pour la timeline')
        commit('SET_EVENTS', [])
        return
      }
      
      commit('SET_CURRENT_PROPERTY_ID', targetPropertyId)
      
      // Récupérer la propriété depuis IndexedDB via Dexie
      const property = await dexieService.getProperty(targetPropertyId)
      
      if (!property) {
        console.warn(`Propriété ${targetPropertyId} non trouvée dans IndexedDB`)
        commit('SET_EVENTS', [])
        return
      }
      
      // Récupérer tous les utilisateurs depuis la base locale pour résoudre les auteurs
      const allUsers = await dexieService.getUsers()
      
      // Récupérer le dictionnaire rentable depuis IndexedDB
      const rentableDictionary = await dexieService.getDictionary('rentable')
      // Le dictionnaire est un objet simple clé-valeur, pas un tableau d'items
      const rentableDict = rentableDictionary || {}
      
      // Transformer les visites en événements de timeline
      const events = []
      
      // 1. Récupérer les visites de property.visits (visites synchronisées)
      if (property.visits && Array.isArray(property.visits)) {
        for (const visit of property.visits) {
          const timelineEvent = await transformVisitToEvent(visit, property, allUsers, rentableDict)
          if (timelineEvent) {
            events.push(timelineEvent)
          }
        }
      }
      
      // 2. Récupérer les visites depuis la table mutations (visites en attente de synchronisation)
      try {
        const { mutations } = await import('@/services/dexieService')
        const pendingVisitMutations = await mutations()
          .filter(mutation => {
            // Filtrer les mutations de type VISIT pour cette propriété
            // Vérifier à la fois mutation.data.propertyId et mutation.data.property.id
            return mutation.entity === 'visit' && 
                   mutation.data && 
                   (mutation.data.propertyId === targetPropertyId || 
                    mutation.data.property?.id === targetPropertyId)
          })
          .toArray()
        
        
        // Transformer les mutations en événements de timeline
        for (const mutation of pendingVisitMutations) {
          const visitFromMutation = {
            id: mutation.data.id || `mutation-${mutation.id}`,
            ...mutation.data,
            createdAt: mutation.insertedAt || mutation.data.createdAt,
            // Marquer comme non synchronisée
            toSync: true,
            fromMutation: true,
            mutationId: mutation.id
          }
          
          const timelineEvent = await transformVisitToEvent(visitFromMutation, property, allUsers, rentableDict)
          if (timelineEvent) {
            // Ajouter une indication visuelle que c'est une visite en attente de sync
            timelineEvent.pending = true
            timelineEvent.mutationId = mutation.id
            events.push(timelineEvent)
          }
        }
      } catch (mutationError) {
        console.warn('Erreur lors de la récupération des mutations de visite:', mutationError)
        // Continue même si la récupération des mutations échoue
      }
      
      // Trier les événements par date décroissante (plus récent d'abord)
      events.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      
      commit('SET_EVENTS', events)
    } catch (error) {
      console.error('Erreur lors du chargement de la timeline:', error)
      commit('SET_ERROR', error.message)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createEvent({ commit }, eventData) {
    try {
      // Simuler un appel API
      const newEvent = {
        id: Date.now().toString(),
        ...eventData,
        createdAt: new Date().toISOString()
      }
      commit('ADD_EVENT', newEvent)
      return newEvent
    } catch (error) {
      commit('SET_ERROR', error.message)
      throw error
    }
  },

  async updateEvent({ commit }, updatedEvent) {
    try {
      // Simuler un appel API
      commit('UPDATE_EVENT', updatedEvent)
    } catch (error) {
      commit('SET_ERROR', error.message)
      throw error
    }
  },

  async deleteEvent({ commit }, eventId) {
    try {
      // Simuler un appel API
      commit('REMOVE_EVENT', eventId)
    } catch (error) {
      commit('SET_ERROR', error.message)
      throw error
    }
  }
}

const getters = {
  events: state => state.events,
  loading: state => state.loading,
  error: state => state.error,
  eventsByType: state => type => {
    return state.events.filter(event => event.type === type)
  },
  eventsByStatus: state => status => {
    return state.events.filter(event => event.status === status)
  },
  sortedEvents: state => {
    return [...state.events].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
  }
}

// Helper function pour transformer une visite en événement de timeline
function transformVisitToEvent(visit, property, allUsers = [], rentableDict = {}) {
  if (!visit || !visit.id) return null
  
  // Construire le message (état du lot - relouabilité)
  let message = ''
  
  // Gérer le format avec summary.rentable (array) pour les visites des mutations (mode offline)
  if (visit.summary && visit.summary.rentable) {
    const selectedRentable = visit.summary.rentable.find(r => r.selected)
    if (selectedRentable) {
      message = `Le logement est relouable ${selectedRentable.label.toLowerCase()}`
    } else {
      message = 'Donnée manquante'
    }
  } else if (visit.rentable) {
    // Format classique pour les visites synchronisées - utiliser le dictionnaire
    // Le dictionnaire est un objet simple clé-valeur: {I: "En l'état", NW: "Avec travaux...", ...}
    // Essayer d'abord avec le dictionnaire, puis avec RENTABLE_MAPPING en fallback
    const rentableValue = rentableDict[visit.rentable] || RENTABLE_MAPPING[visit.rentable]
    message = rentableValue 
      ? `Le logement est relouable ${rentableValue.toLowerCase()}` 
      : 'Donnée manquante'
  } else {
    message = 'Donnée manquante'
  }
  
  // Récupérer le nom de l'auteur (priorité à technicalManager, puis author)
  let author = 'Utilisateur'
  
  // Essayer d'abord avec technicalManager (source principale)
  const authorSource = visit.technicalManager || visit.author
  
  if (authorSource) {
    // Si authorSource est un objet avec fullName, firstName, lastName
    if (typeof authorSource === 'object' && authorSource !== null) {
      const fullName = authorSource.fullName || 
                       `${authorSource.firstName || ''} ${authorSource.lastName || ''}`.trim()
      author = fullName || 'Utilisateur'
    } 
    // Si authorSource est un ID (string ou number), chercher dans la table users
    else if (typeof authorSource === 'string' || typeof authorSource === 'number') {
      const userId = String(authorSource)
      const userFromDb = allUsers.find(u => String(u.id) === userId)
      
      if (userFromDb) {
        const fullName = userFromDb.fullName || 
                         `${userFromDb.firstName || ''} ${userFromDb.lastName || ''}`.trim()
        author = fullName || 'Utilisateur'
      }
    }
  }
  
  // Vérifier si la visite a été créée hors ligne ou est en attente de sync
  const createdOffline = visit.createdOffline === true || visit.offline === true || visit.toSync === true || visit.fromMutation === true
  
  return {
    id: visit.id,
    visit: visit.id, // ID de la visite pour les boutons
    createdAt: visit.createdAt || visit.timestamp || new Date().toISOString(),
    author,
    message,
    createdOffline,
    pending: visit.toSync || visit.fromMutation, // Indique si la visite est en attente de synchronisation
    mutationId: visit.mutationId, // ID de la mutation si applicable
    propertyId: property.id,
    // Données brutes pour accès détaillé
    rawData: visit
  }
}

export default { 
  namespaced: true, 
  state, 
  mutations, 
  actions, 
  getters 
}
